/**
 * Ranch / club standing groups that are not D1 places.
 *
 * Keep this honest: last-Thursday book club and WGA structure are
 * published; a Facebook group URL is not, so we do not invent one.
 */

export interface CommunityClub {
  id: string;
  interestId: string;
  name: string;
  summary: string;
  details: string[];
  joinLabel: string;
  joinNote: string;
  href?: string;
  phone?: string;
  sourceNote: string;
}

export const COMMUNITY_CLUBS: CommunityClub[] = [
  {
    id: 'take-a-break-book-club',
    interestId: 'book-club',
    name: 'Take a Break Book Club',
    summary:
      'Ranch-wide, multi-generational book club. Last Thursday evening of the month — snacks, wine, and the book if you finished it.',
    details: [
      'Open across Babcock Ranch — not a Babcock National amenity. Multi-generational, including young parents, working residents, retirees and grandparents.',
      'Meets at rotating members’ homes, not a lodge booking. Confirm the month’s host and book on Neighbors of Babcock Ranch Facebook or the Telegraph.',
      'It started as a Neighbors of Babcock Ranch Facebook post. No public join URL is published — we do not invent one.',
    ],
    joinLabel: 'Neighbors Facebook',
    joinNote:
      'Search Facebook for Neighbors of Babcock Ranch and Take a Break Book Club. No public join URL is published — we do not invent one.',
    sourceNote:
      'Babcock Ranch Telegraph, “Take a Break Book Club goes by the book,” plus later Telegraph profiles. Last-Thursday cadence from that reporting.',
  },
  {
    id: 'bnwga',
    interestId: 'wga',
    name: 'Babcock National WGA',
    summary:
      'Women’s Golf Association at Babcock National: 18-hole competitive play and a 9-hole non-competitive skill-development group.',
    details: [
      'Members only — Golf or Transfer membership plus a GHIN registered at Babcock National. Max handicap 40. Guests are not in unless a specific week’s schedule says so.',
      '18-hole (BNWGA): competitive weekly on Thursdays, November to April. See Golf Genius — the 2026-27 calendar mostly shows 8:06 AM tee times, with some 8:30 AM shotguns. $50 annual fee + regular golf fee + prize pool.',
      '9-hole: Women’s 9-Hole League on Thursdays from 1:03 PM, November to April. Non-competitive skill development after the 18-holers. Regular golf fee + $5 prize pool. Better for newer players — not for absolute first-timers.',
      'True beginners: skip league week one. PGA clinics (≤5, any skill) are $20 member / $30 non-member; private lessons also run through the Golf Shop — (941) 347-2200. Head Golf Professional: Matt Gagnon, PGA.',
      'The course is public for play and instruction. National membership itself is owners-only.',
      'To register, use the member website (babcocknationalgc.com, under Golf Leagues & Events, then League Directory) or Golf Genius (web or app). Ask the Golf Shop for the current league link.',
    ],
    joinLabel: 'Golf shop',
    joinNote: 'Call the Golf Shop to ask about WGA signup, GHIN, and beginner clinics.',
    href: '/babcock/national#wga',
    phone: '9413472200',
    sourceNote:
      'BNGC Welcome Package 2026-27 for the November to April season, Thursday times, $50 annual BNWGA fee, and Head Golf Professional Matt Gagnon. GHIN and members-only carried forward; clinic prices from babcocknationalgc.com course-details (docs/babcock-national-reference.md).',
  },
];

export function clubById(id: string): CommunityClub | undefined {
  return COMMUNITY_CLUBS.find((c) => c.id === id);
}

export const TAKE_A_BREAK_BOOK_CLUB = COMMUNITY_CLUBS[0];
export const BABCOCK_NATIONAL_WGA = COMMUNITY_CLUBS[1];

export function clubsForInterestIds(ids: string[]): CommunityClub[] {
  const want = new Set(ids);
  return COMMUNITY_CLUBS.filter((c) => want.has(c.interestId));
}
